import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`
  const seconds = Math.floor(ms / 1000)
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes}m ${remainingSeconds}s`
}

export function formatRelativeTime(date: Date): string {
  const now = new Date()
  const diffMs = now.getTime() - new Date(date).getTime()
  // #1218 Group E: support future timestamps too ("in 3d"). The legacy
  // helper bucketed every sub-minute |diff| as "just now"; we preserve
  // that for clock-skew tolerance (a few seconds in either direction
  // reads identically). For larger future diffs, mirror the past
  // bucketing with "in <N><unit>".
  const absSeconds = Math.floor(Math.abs(diffMs) / 1000)
  const absMinutes = Math.floor(absSeconds / 60)
  const absHours = Math.floor(absMinutes / 60)
  const absDays = Math.floor(absHours / 24)
  const future = diffMs < 0

  if (absSeconds < 60) return 'just now'
  const fmt = (n: number, unit: 'm' | 'h' | 'd') =>
    future ? `in ${n}${unit}` : `${n}${unit} ago`
  if (absMinutes < 60) return fmt(absMinutes, 'm')
  if (absHours < 24) return fmt(absHours, 'h')
  if (absDays < 7) return fmt(absDays, 'd')

  // >7d fallback: tz-irrelevant by design (approximate date is fine
  // for week-plus-old timestamps). Phase 2 of #724 may revisit if a
  // call site cares — none today do.
  return new Date(date).toLocaleDateString()
}

/**
 * Date / time / date-time formatters that honour an explicit IANA
 * timezone. Phase 1 of #724 — pair with `useUserTimezone()` to render
 * timestamps in the signed-in user's profile timezone instead of the
 * browser's local one.
 *
 * Each helper:
 *   - Accepts the same input shapes the existing `new Date(...)`
 *     call sites pass (Date, ISO string, epoch millis).
 *   - Defaults to the browser's locale (matches the existing
 *     `.toLocale*String()` call sites — passing 'en-US' would visually
 *     change formatting for non-US users and that's not the bug we're
 *     fixing here).
 *   - Returns an em-dash for nullish input and the raw string form
 *     for unparseable input — same defensive pattern as the audit
 *     log's `formatTsSafe` (added in PR #530 Copilot review).
 *
 * The `tz` parameter is required, not optional, by design: an
 * accidentally-omitted timezone would silently revert to browser-local
 * rendering — defeating the whole point of the migration. Callers
 * either pass `useUserTimezone()` (the common case) or an explicit
 * zone they want (e.g. `'UTC'` for "consistent across viewers"
 * surfaces like the analytics drill-down's time axis).
 */
export function formatDate(
  date: Date | string | number | null | undefined,
  tz: string,
): string {
  const d = toDateOrNull(date)
  if (d === null) return '—'
  if (Number.isNaN(d.getTime())) return String(date)
  return d.toLocaleDateString(undefined, { timeZone: tz })
}

export function formatTime(
  date: Date | string | number | null | undefined,
  tz: string,
): string {
  const d = toDateOrNull(date)
  if (d === null) return '—'
  if (Number.isNaN(d.getTime())) return String(date)
  return d.toLocaleTimeString(undefined, { timeZone: tz })
}

/**
 * Date + time together, in the operator's preferred IANA zone — the
 * tz-aware analogue of a bare `new Date(x).toLocaleString()` call.
 *
 * #1227 frontend sweep — most of the migrated call sites previously
 * rendered both date and time via `toLocaleString()` (browser-local).
 * Composing `formatDate(d, tz) + ' ' + formatTime(d, tz)` at every call
 * site would read noisily; a single helper keeps the call shape
 * one-line and the intent ("show this stamp in the user's tz") obvious.
 *
 * Uses the locale's natural date-time concatenation (the same
 * `toLocaleString` engine, just with `timeZone` pinned). The returned
 * string is identical in shape to what the bare call produced — only
 * the zone changes.
 *
 * Convention reminder (see `formatDateTime` above):
 *   - UTC-by-design surfaces (audit, scheduled run views, run-report
 *     headers, deploy timestamps, billing-period boundaries) use
 *     `formatDateTime(d, 'absoluteUTC')` — NOT this helper.
 *   - Relative / live surfaces (run lists, activity log recency) use
 *     `formatRelativeTime` / `formatDateTime(d, 'relative')`.
 *   - Everything else — operator-personalization layer where "what's
 *     my local time for this event?" is the right question — uses this
 *     helper (or `formatDate` / `formatTime` directly).
 */
export function formatLocalDateTime(
  date: Date | string | number | null | undefined,
  tz: string,
): string {
  const d = toDateOrNull(date)
  if (d === null) return '—'
  if (Number.isNaN(d.getTime())) return String(date)
  return d.toLocaleString(undefined, { timeZone: tz })
}

/**
 * Convention-driven timestamp formatter. #1218 Group E.
 *
 * Modes:
 *   - `'relative'` — "just now", "5m ago", "2h ago", "3d ago". Used
 *     for **recent / live** surfaces — run lists, activity log,
 *     dashboard recency. "When did this happen?" is the operator's
 *     question; the answer is friendlier in relative terms anchored
 *     to "now".
 *   - `'absoluteUTC'` — "2026-06-06 12:34 UTC". Used for **forensic /
 *     scheduling / archival** surfaces — audit log, scheduled run
 *     views, run-report headers. The operator may be cross-referencing
 *     across timezones (support tickets, deploy schedules, audit reads),
 *     and a UTC stamp is the only unambiguous answer. 24-hour clock
 *     by design — the issue explicitly defers a 12h preference setting.
 *   - `'short'` — "Jun 6 12:34" (year omitted for current-year dates).
 *     Used for **table cells with limited width** where precision
 *     matters less than fit. No timezone label; an inline header
 *     should disclose the renderer's tz context if cross-tz reading
 *     is plausible.
 *
 * Why a single helper rather than three exports: callers pick the mode
 * by intent, and the mode signature lets a reviewer trace "is this
 * call site choosing the right convention?" by reading the second arg
 * at the call site instead of cross-referencing imports.
 *
 * For tz-aware rendering in the user's preferred zone (the prior
 * Phase 1 #724 contract), call sites should use `formatDate` /
 * `formatTime` with an explicit IANA tz argument — those helpers
 * are unchanged.
 */
export type FormatDateTimeMode = 'relative' | 'absoluteUTC' | 'short'

export function formatDateTime(
  date: Date | string | number | null | undefined,
  mode: FormatDateTimeMode,
): string {
  const d = toDateOrNull(date)
  if (d === null) return '—'
  if (Number.isNaN(d.getTime())) return String(date)

  if (mode === 'relative') return formatRelativeTime(d)
  if (mode === 'absoluteUTC') return formatAbsoluteUTC(d)
  return formatShort(d)
}

/** "2026-06-06 12:34 UTC" — built deterministically from the date's
 * UTC field accessors, then the literal "UTC" suffix. The whole point
 * of this mode is "same string for every viewer regardless of locale
 * / tz / host ICU version", so we deliberately avoid `Intl.DateTimeFormat`
 * (whose output drifts across hosts even with `en-CA`). */
function formatAbsoluteUTC(d: Date): string {
  const yyyy = d.getUTCFullYear().toString().padStart(4, '0')
  const mm = (d.getUTCMonth() + 1).toString().padStart(2, '0')
  const dd = d.getUTCDate().toString().padStart(2, '0')
  const hh = d.getUTCHours().toString().padStart(2, '0')
  const mn = d.getUTCMinutes().toString().padStart(2, '0')
  return `${yyyy}-${mm}-${dd} ${hh}:${mn} UTC`
}

/** "Jun 6 12:34" (current year) or "Jun 6 2025 12:34" (other years).
 * Browser-local timezone — caller is expected to disclose tz context
 * in the surrounding header if cross-tz reading is plausible. */
function formatShort(d: Date): string {
  const now = new Date()
  const sameYear = d.getFullYear() === now.getFullYear()
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: sameYear ? undefined : 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

function toDateOrNull(
  date: Date | string | number | null | undefined,
): Date | null {
  if (date === null || date === undefined) return null
  // Empty string maps to nullish, not "Invalid Date" — matches the
  // defensive contract the audit log's original `formatTsSafe` carried.
  if (typeof date === 'string' && date.trim() === '') return null
  if (date instanceof Date) return date
  return new Date(date)
}

export function getEnvironmentColor(status: string): string {
  switch (status) {
    case 'production':
      return 'text-green-500'
    case 'staging':
      return 'text-yellow-500'
    case 'development':
      return 'text-blue-500'
    default:
      return 'text-purple-500'
  }
}

export function getEnvironmentBgColor(status: string): string {
  switch (status) {
    case 'production':
      return 'bg-green-500/10 text-green-600 dark:text-green-400'
    case 'staging':
      return 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400'
    case 'development':
      return 'bg-blue-500/10 text-blue-600 dark:text-blue-400'
    default:
      return 'bg-purple-500/10 text-purple-600 dark:text-purple-400'
  }
}

export function getStatusColor(status: string): string {
  switch (status) {
    case 'passed':
      return 'text-success'
    case 'failed':
      return 'text-destructive'
    case 'running':
      return 'text-primary'
    case 'skipped':
      return 'text-muted-foreground'
    default:
      return 'text-muted-foreground'
  }
}
